import { useEffect, useMemo, useState } from 'react'
import { useHubLang } from '../lib/i18n'
import { PHOTO_ROOM } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import { photoSitesFor, setPhotoSite, siteOptions, type PhotoSite, type SiteOption } from '../lib/photoRoom'
import { SearchPick } from './SearchPick'

/**
 * 321 (FED-74). The site a photo was taken at — a mine, port, dam, wind farm,
 * highway project — in the Gallery viewer's details, for Gallery document
 * users and admins (David, 29 Oct; before 321 it was the admin's alone).
 *
 * OneConetec guesses it from where the photo was taken and what is in it,
 * and says how sure it is. Anyone here may say yes, say no (it is not a named
 * site), or pick the right one from the site list by typing — or type a new
 * name, which joins the list. What a person says stands: OneConetec never
 * guesses over it. The server decides who may (set_photo_site); this row only
 * appears when photo_sites_for answered for the photo, which it does for a
 * photo with a location in the caller's Gallery.
 *
 * A <dt>/<dd> pair: it sits in the viewer's details list.
 */
export function PhotoSiteRow({ path, tenantId }: { path: string; tenantId: string }) {
  const t = PHOTO_ROOM[useHubLang()]
  const [site, setSite] = useState<PhotoSite | null | undefined>(undefined)
  const [options, setOptions] = useState<SiteOption[] | null>(null)
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rev, setRev] = useState(0)

  useEffect(() => {
    let live = true
    setSite(undefined)
    setPicking(false)
    setError(null)
    photoSitesFor([path])
      .then((m) => live && setSite(m.get(path) ?? null))
      .catch(() => live && setSite(null))
    return () => {
      live = false
    }
  }, [path, rev])

  // The list is read when somebody opens the picker, not for every photo.
  useEffect(() => {
    if (!picking || options) return
    let live = true
    siteOptions()
      .then((o) => live && setOptions(o))
      .catch(() => live && setOptions([]))
    return () => {
      live = false
    }
  }, [picking, options])

  const offer = useMemo(
    () =>
      (options ?? [])
        .filter((o) => o.tenantId === tenantId && !(site?.status === 'accepted' && o.id === site.siteId))
        .map((o) => ({ ...o, hint: t.sitePhotos(o.photos) })),
    [options, tenantId, site, t],
  )

  if (site === null) return null

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setPicking(false)
      setOptions(null)
      setRev((r) => r + 1)
    } catch (e) {
      setError(messageOf(e) || t.siteFailed)
    } finally {
      setBusy(false)
    }
  }

  const pct = site?.confidence != null ? Math.round(site.confidence * 100) : 50
  return (
    <>
      <dt>{t.site}</dt>
      <dd className="photosite">
        {site === undefined ? (
          '…'
        ) : (
          <>
            {site.status === 'suggested' && site.site && (
              <>
                <span className="photosite__name">{site.site}</span>{' '}
                <span className="roomviewer__note">({t.siteSuggested(pct)})</span>{' '}
                <button type="button" className="btn btn--link" disabled={busy} onClick={() => void act(() => setPhotoSite(path, true))}>
                  {t.siteYes}
                </button>{' '}
                <button type="button" className="btn btn--link" disabled={busy} onClick={() => void act(() => setPhotoSite(path, false))}>
                  {t.siteNo}
                </button>
              </>
            )}
            {site.status === 'accepted' && site.site && (
              <>
                <span className="photosite__name">{site.site}</span>{' '}
                <span className="roomviewer__note">{t.siteConfirmedBy(site.decidedBy)}</span>
              </>
            )}
            {site.status === 'rejected' && <span className="roomviewer__note">{t.siteNoneSaid(site.decidedBy)}</span>}
            {site.status === null && (
              <span className="roomviewer__note">{site.oneHasLooked ? t.siteOneUnsure : t.siteNotYet}</span>
            )}{' '}
            {!picking && (
              <button type="button" className="btn btn--link" disabled={busy} onClick={() => setPicking(true)}>
                {site.site && site.status !== 'rejected' ? t.siteChange : t.siteRename}
              </button>
            )}
            {picking && (
              <span className="photosite__pick">
                <SearchPick
                  options={offer}
                  label={t.sitePick}
                  autoFocus
                  disabled={busy || options === null}
                  onPick={(o) => void act(() => setPhotoSite(path, true, { siteId: o.id }))}
                  onCreate={(text) => void act(() => setPhotoSite(path, true, { newName: text }))}
                />
                <button type="button" className="btn btn--quiet btn--sm" onClick={() => setPicking(false)}>
                  {t.cancel}
                </button>
              </span>
            )}
            {error && (
              <span className="photosite__err" role="alert">
                {' '}
                {error}
              </span>
            )}
          </>
        )}
      </dd>
    </>
  )
}
